/*
 * session.js — the document model for /pdf-editor.
 *
 * WHY A MODEL AND NOT JUST BYTES
 * ──────────────────────────────
 * Every online PDF tool surveyed on 2026-08-10 is a pipeline: upload → one
 * operation → download. Rotating a page and then adding a signature means
 * uploading twice, and the second upload knows nothing about the first. The
 * user pays for that in re-uploads, and the document pays for it in a second
 * full re-encode.
 *
 * This module is the alternative: ONE document held in the browser, with every
 * operation expressed as a change to a model, and exactly one encode at the
 * end. Rotate, delete, reorder, annotate, sign, redact — all of it lands here
 * first and hits pdf-lib once.
 *
 * THE INVARIANT THAT MATTERS
 * ──────────────────────────
 * Objects anchor to a page's STABLE ID, never to its position. Deleting page 2
 * must not slide the signature on page 3 onto page 4 — that is silent document
 * corruption, and position-indexed anchoring is exactly how it happens. Page
 * order is a view (`order`), page identity is permanent (`id`).
 *
 * Placements are FRACTIONS of the page box, reusing the convention proven in
 * /sign-pdf (public/js/pdf-coords.js): a placement stored as {nx,ny,nw,nh} in
 * [0,1] re-projects correctly at any zoom, any devicePixelRatio, any render
 * scale. Storing pixels instead is the other classic corruption.
 *
 * Pure: no DOM, no pdf.js, no pdf-lib, no globals. That is what lets
 * tests/js/pdf-editor-session.test.mjs lock the ordering and anchoring rules
 * without a browser.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.PdfSession = api;
})(typeof window !== 'undefined' ? window : null, function () {
    'use strict';

    var MAX_HISTORY = 60;

    function norm360(d) { return ((d % 360) + 360) % 360; }
    function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
    function clone(v) { return JSON.parse(JSON.stringify(v)); }

    /* ── construction ───────────────────────────────────────────────────── */

    /* `sourcePages` describes the opened PDF: one entry per page, in file
       order, carrying the intrinsic box and the file's own /Rotate. */
    function create(sourcePages) {
        var pages = (sourcePages || []).map(function (p, i) {
            return {
                id: 'p' + i,            // stable for the life of the session
                src: i,                 // index in the ORIGINAL file
                width: p.width,         // MediaBox width in points (unrotated)
                height: p.height,
                baseRotate: norm360(p.rotate || 0),   // the file's own /Rotate
                turn: 0,                // user rotation ON TOP of baseRotate
                inserted: false,        // true for pages this session created
            };
        });
        return {
            pages: pages,
            order: pages.map(function (p) { return p.id; }),
            objects: [],
            seq: 0,
            _undo: [],
            _redo: [],
        };
    }

    /* ── page queries ───────────────────────────────────────────────────── */

    function pageById(st, id) {
        for (var i = 0; i < st.pages.length; i++) if (st.pages[i].id === id) return st.pages[i];
        return null;
    }

    /* Pages in display order. Deleted pages are simply absent from `order`,
       which keeps their objects intact for undo. */
    function visiblePages(st) {
        return st.order.map(function (id) { return pageById(st, id); }).filter(Boolean);
    }

    function pageCount(st) { return st.order.length; }

    /* Total clockwise rotation a viewer will apply: the file's own plus the
       user's. Callers render and export against this single number. */
    function effectiveRotate(page) { return norm360(page.baseRotate + page.turn); }

    /* Page box as the user sees it — width/height swap on quarter turns. */
    function displayBox(page) {
        var r = effectiveRotate(page);
        var swap = (r === 90 || r === 270);
        return {
            width: swap ? page.height : page.width,
            height: swap ? page.width : page.height,
            rotate: r,
        };
    }

    /* ── history ────────────────────────────────────────────────────────── */

    /* Snapshot-based undo. The model is small (page list + object list), so a
       snapshot costs far less than the bug surface of inverse operations —
       and an editor that loses a page on undo is worse than a slow one. */
    function snapshot(st) {
        return { pages: clone(st.pages), order: st.order.slice(), objects: clone(st.objects), seq: st.seq };
    }

    function restore(st, snap) {
        st.pages = clone(snap.pages);
        st.order = snap.order.slice();
        st.objects = clone(snap.objects);
        st.seq = snap.seq;
    }

    function commit(st) {
        st._undo.push(snapshot(st));
        if (st._undo.length > MAX_HISTORY) st._undo.shift();
        st._redo.length = 0;
    }

    function undo(st) {
        if (!st._undo.length) return false;
        var prev = st._undo.pop();
        st._redo.push(snapshot(st));
        restore(st, prev);
        return true;
    }

    function redo(st) {
        if (!st._redo.length) return false;
        var next = st._redo.pop();
        st._undo.push(snapshot(st));
        restore(st, next);
        return true;
    }

    function canUndo(st) { return st._undo.length > 0; }
    function canRedo(st) { return st._redo.length > 0; }

    /* ── page operations ────────────────────────────────────────────────── */

    /* Turning a page must carry its annotations with it. Placements are
       fractions of the DISPLAYED page, so leaving them alone would keep a
       highlight where it was on screen while the text it marks rotates out
       from under it — wrong document, no error. `geom` is injected rather than
       imported so this module stays dependency-free and testable on its own. */
    function reprojectObjects(st, pageId, delta, geom) {
        if (!geom) return;
        st.objects.forEach(function (o) {
            if (o.page !== pageId) return;
            o.placement = geom.rotatePlacement(o.placement, delta);
            if (o.points) o.points = geom.rotatePoints(o.points, delta);
        });
    }

    function rotatePage(st, id, delta, geom) {
        var p = pageById(st, id);
        if (!p) return false;
        commit(st);
        p.turn = norm360(p.turn + delta);
        reprojectObjects(st, id, delta, geom);
        return true;
    }

    function rotateAll(st, delta, geom) {
        commit(st);
        visiblePages(st).forEach(function (p) {
            p.turn = norm360(p.turn + delta);
            reprojectObjects(st, p.id, delta, geom);
        });
        return true;
    }

    /* Removing a page hides it from `order` but keeps the page record and its
       objects, so undo restores the annotations too — not a blank page back. */
    function deletePage(st, id) {
        var at = st.order.indexOf(id);
        if (at < 0 || st.order.length <= 1) return false;   // never empty the doc
        commit(st);
        st.order.splice(at, 1);
        return true;
    }

    function movePage(st, id, toIndex) {
        var from = st.order.indexOf(id);
        if (from < 0) return false;
        var to = Math.max(0, Math.min(st.order.length - 1, toIndex));
        if (from === to) return false;
        commit(st);
        st.order.splice(from, 1);
        st.order.splice(to, 0, id);
        return true;
    }

    /* Add a page that did not come from the source file (blank, or imported
       from another PDF). `src` stays null so export knows to create it. */
    function insertPage(st, spec, atIndex) {
        commit(st);
        var id = 'n' + (st.seq++);
        st.pages.push({
            id: id,
            src: (spec && typeof spec.src === 'number') ? spec.src : null,
            external: (spec && spec.external) || null,   // {docKey, index} for imports
            width: (spec && spec.width) || 595,
            height: (spec && spec.height) || 842,
            baseRotate: 0,
            turn: 0,
            inserted: true,
        });
        var to = (atIndex === undefined || atIndex === null)
            ? st.order.length
            : Math.max(0, Math.min(st.order.length, atIndex));
        st.order.splice(to, 0, id);
        return id;
    }

    /* Restore a previously deleted page at its old neighbourhood if possible. */
    function restorePage(st, id, atIndex) {
        if (st.order.indexOf(id) >= 0) return false;
        if (!pageById(st, id)) return false;
        commit(st);
        var to = (atIndex === undefined || atIndex === null)
            ? st.order.length
            : Math.max(0, Math.min(st.order.length, atIndex));
        st.order.splice(to, 0, id);
        return true;
    }

    /* ── objects ────────────────────────────────────────────────────────── */

    /* Every object carries `page` (a stable page id) and `placement` in page
       fractions. `z` orders drawing within a page. */
    function addObject(st, obj) {
        if (!obj || !obj.type || !obj.page) return null;
        if (!pageById(st, obj.page)) return null;
        commit(st);
        var o = clone(obj);
        o.id = 'o' + (st.seq++);
        o.placement = normalizePlacement(o.placement);
        if (typeof o.z !== 'number') o.z = nextZ(st, o.page);
        st.objects.push(o);
        return o.id;
    }

    function normalizePlacement(p) {
        p = p || {};
        return {
            nx: clamp01(p.nx || 0),
            ny: clamp01(p.ny || 0),
            nw: clamp01(p.nw === undefined ? 0.2 : p.nw),
            nh: clamp01(p.nh === undefined ? 0.06 : p.nh),
            rot: norm360(p.rot || 0),
        };
    }

    function nextZ(st, pageId) {
        var max = 0;
        for (var i = 0; i < st.objects.length; i++) {
            if (st.objects[i].page === pageId && st.objects[i].z > max) max = st.objects[i].z;
        }
        return max + 1;
    }

    function objectById(st, id) {
        for (var i = 0; i < st.objects.length; i++) if (st.objects[i].id === id) return st.objects[i];
        return null;
    }

    /* Objects on a page, in draw order. */
    function objectsOn(st, pageId) {
        return st.objects.filter(function (o) { return o.page === pageId; })
                         .sort(function (a, b) { return a.z - b.z; });
    }

    /* `history:false` lets a drag emit continuous updates without flooding the
       undo stack — the caller commits once when the gesture starts. */
    function updateObject(st, id, patch, opts) {
        var o = objectById(st, id);
        if (!o) return false;
        if (!opts || opts.history !== false) commit(st);
        Object.keys(patch).forEach(function (k) {
            if (k === 'id') return;
            if (k === 'placement') o.placement = normalizePlacement(patch.placement);
            else o[k] = patch[k];
        });
        return true;
    }

    function removeObject(st, id) {
        var at = -1;
        for (var i = 0; i < st.objects.length; i++) if (st.objects[i].id === id) { at = i; break; }
        if (at < 0) return false;
        commit(st);
        st.objects.splice(at, 1);
        return true;
    }

    /* Move an object to another page, keeping its fractional placement — which
       is what the user means when they drag a stamp across a page boundary. */
    function moveObjectToPage(st, id, pageId) {
        var o = objectById(st, id);
        if (!o || !pageById(st, pageId)) return false;
        commit(st);
        o.page = pageId;
        o.z = nextZ(st, pageId);
        return true;
    }

    function raise(st, id) {
        var o = objectById(st, id);
        if (!o) return false;
        commit(st);
        o.z = nextZ(st, o.page);
        return true;
    }

    function lower(st, id) {
        var o = objectById(st, id);
        if (!o) return false;
        commit(st);
        var min = Infinity;
        objectsOn(st, o.page).forEach(function (x) { if (x.z < min) min = x.z; });
        o.z = (min === Infinity ? 1 : min) - 1;
        return true;
    }

    /* ── state summary ──────────────────────────────────────────────────── */

    /* Has the user actually changed anything? Drives the "you have unsaved
       work" guard and whether export is worth offering. */
    function isDirty(st) {
        if (st.objects.length) return true;
        if (st.order.length !== st.pages.filter(function (p) { return !p.inserted; }).length) return true;
        for (var i = 0; i < st.pages.length; i++) {
            if (st.pages[i].turn !== 0) return true;
            if (st.pages[i].inserted) return true;
        }
        // reordered?
        for (var j = 0; j < st.order.length; j++) {
            if (st.order[j] !== 'p' + j) return true;
        }
        return false;
    }

    function stats(st) {
        var byType = {};
        st.objects.forEach(function (o) { byType[o.type] = (byType[o.type] || 0) + 1; });
        return {
            pages: st.order.length,
            sourcePages: st.pages.filter(function (p) { return !p.inserted; }).length,
            objects: st.objects.length,
            byType: byType,
            dirty: isDirty(st),
        };
    }

    return {
        create: create,
        // pages
        pageById: pageById,
        visiblePages: visiblePages,
        pageCount: pageCount,
        effectiveRotate: effectiveRotate,
        displayBox: displayBox,
        rotatePage: rotatePage,
        rotateAll: rotateAll,
        deletePage: deletePage,
        movePage: movePage,
        insertPage: insertPage,
        restorePage: restorePage,
        // objects
        addObject: addObject,
        objectById: objectById,
        objectsOn: objectsOn,
        updateObject: updateObject,
        removeObject: removeObject,
        moveObjectToPage: moveObjectToPage,
        raise: raise,
        lower: lower,
        normalizePlacement: normalizePlacement,
        // history
        commit: commit,
        undo: undo,
        redo: redo,
        canUndo: canUndo,
        canRedo: canRedo,
        // state
        isDirty: isDirty,
        stats: stats,
    };
});
